import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, Eye, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import SaveButton from "../../components/admin/SaveButton";
import { useUploadStatus } from "../../context/UploadStatusContext";

const empty = { name: "", description: "", long_description: "", image_url: "", section: "standard", order: 0, is_active: true };

export default function AdminFlavours() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const { isUploading } = useUploadStatus();

    const load = () => api.get("/flavours?active_only=false").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing("new"); setForm(empty); };
    const openEdit = (f) => { setEditing(f.id); setForm(f); };
    const close = () => { setEditing(null); setForm(empty); };

    const save = async (e) => {
        e.preventDefault();
        if (isUploading) return;
        try {
            if (editing === "new") await api.post("/flavours", form);
            else await api.put(`/flavours/${editing}`, form);
            toast.success("Saved");
            close();
            load();
        } catch (err) {
            toast.error("Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this flavour?")) return;
        try { await api.delete(`/flavours/${id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    const toggleActive = async (f) => {
        try {
            await api.put(`/flavours/${f.id}`, { ...f, is_active: !f.is_active });
            toast.success(f.is_active ? "Disabled — hidden from site" : "Enabled — now visible");
            load();
        } catch { toast.error("Update failed"); }
    };

    return (
        <div data-testid="admin-flavours">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-3xl">Flavours</h2>
                <button onClick={openNew} className="btn-pink" data-testid="add-flavour">
                    <Plus size={14} /> New
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((f, i) => (
                    <div key={f.id} className="bg-white border border-[#EAE3DE] p-4 flex gap-4" data-testid={`flavour-row-${i}`}>
                        {f.image_url ? (
                            <img src={f.image_url} alt={f.name} className="w-20 h-20 object-cover flex-shrink-0 bg-blush-pale" />
                        ) : (
                            <div className="w-20 h-20 flex-shrink-0 bg-blush-pale flex items-center justify-center text-[8px] tracking-[0.2em] uppercase text-blush/60">no image</div>
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <p className={`font-display text-lg truncate ${f.is_active ? "" : "opacity-50"}`}>{f.name}</p>
                                <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 shrink-0 ${
                                    (f.section || "standard") === "gourmet"
                                        ? "bg-blush text-white"
                                        : "bg-[#EAE3DE] text-ink"
                                }`}>
                                    {(f.section || "standard") === "gourmet" ? "Gourmet" : "Standard"}
                                </span>
                                {!f.is_active && <span className="text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 shrink-0 bg-gray-100 text-gray-500">Hidden</span>}
                            </div>
                            <p className="text-xs text-ink-soft line-clamp-2 mb-2">{f.description}</p>
                            <div className="flex gap-3 items-center">
                                <button onClick={() => toggleActive(f)} className={`text-xs ${f.is_active ? "text-green-600" : "text-gray-400"} hover:text-blush`} title={f.is_active ? "Disable (hide from site)" : "Enable (show on site)"} data-testid={`toggle-flavour-${i}`}>
                                    {f.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
                                </button>
                                <button onClick={() => openEdit(f)} className="text-xs text-ink hover:text-blush" data-testid={`edit-flavour-${i}`}><Edit size={14} /></button>
                                <button onClick={() => remove(f.id)} className="text-xs text-ink hover:text-blush" data-testid={`delete-flavour-${i}`}><Trash2 size={14} /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-display text-2xl">{editing === "new" ? "New" : "Edit"} Flavour</h3>
                            <button onClick={close}><X size={20} /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Name</label>
                                <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="e.g. Red Velvet" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Description (short — shown on the card)</label>
                                <textarea className="w-full px-4 py-3 border border-[#EAE3DE]" rows={3} placeholder="Describe the flavour…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">About this flavour (optional — shown on its own page)</label>
                                <textarea className="w-full px-4 py-3 border border-[#EAE3DE]" rows={5} placeholder="Write more about this flavour — ingredients, pairing suggestions, story…" value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Section</label>
                                <select
                                    className="w-full px-4 py-3 border border-[#EAE3DE] bg-white"
                                    value={form.section || "standard"}
                                    onChange={(e) => setForm({ ...form, section: e.target.value })}
                                    data-testid="flavour-section"
                                >
                                    <option value="standard">Standard Flavours</option>
                                    <option value="gourmet">Gourmet Flavours</option>
                                </select>
                            </div>
                            <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} label="Flavour Image (square, ~800×800)" testIdPrefix="flavour-image" />
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Sort order</label>
                                    <input type="number" className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="0" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
                                </div>
                                <label className="flex items-end gap-2 pb-3">
                                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                    <span className="text-sm">Active</span>
                                </label>
                            </div>
                            <SaveButton>Save Flavour</SaveButton>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
